import { useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  PanelLeftClose,
  PanelLeft,
  BookOpen,
  LifeBuoy,
  Star,
  Search,
  FolderKanban,
  ChevronsUpDown,
  Grip,
  Globe,
  CircleUser,
  Menu,
  X,
} from 'lucide-react';
import {
  AppShell,
  AppShellBody,
  AppMain,
  Topbar,
  ContextualSidebar,
  AppPanel,
  useAppShell,
  type ContextualNavSection,
  type ContextualNavEntry,
} from '@vnetwork/ui/layout';
import {
  IconButton,
  Button,
  Badge,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@vnetwork/ui/primitives';
import { useAuth } from '@/auth/AuthProvider';
import { useMyProjects } from '@/api/projects';
import { useCdnUmbrellas } from '@/api/manticore/cdn-providers';
import { SERVICE_CATEGORIES, SERVICE_GROUPS, brandServiceCategories } from '@/pages/services/categories';
import { useServicesNavTree } from '@/pages/services/use-services-nav-tree';
import { useMyServices } from '@/api/services';
import { isWaapCapable, waapConsoleRoute } from '@/pages/services/waap/waap-service';
import { useWaapCurrentPackage, WAAP_PLANS } from '@/api/waap/settings';
import {
  PinnedServicesProvider,
  usePinnedServices,
  usePinnedLeaves,
  useCurrentServiceLeaf,
} from './pinned-services';
import { PRIMARY_NAV, isActive } from './nav';
import { NavProvider, useContextualNav } from './nav-context';
import { UserMenu } from './UserMenu';
import { CommandMenu } from './CommandMenu';
// NOTE: the live-chat assistant (AskAiPanel + useAskAi) is intentionally kept in
// the tree but no longer mounted — the topbar button now opens the Documentation
// panel instead. Restore the button below to bring the assistant back.
import { DocsPanel } from './DocsPanel';
import { LiveChatWidget } from '@/components/livechat/LiveChatWidget';
import { brand } from '@/config/brand';
import styles from './AppLayout.module.css';

function CollapseToggle() {
  const { collapsed, toggle } = useAppShell();
  return (
    <IconButton
      aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      variant="ghost"
      onClick={toggle}
    >
      {collapsed ? <PanelLeft size={18} /> : <PanelLeftClose size={18} />}
    </IconButton>
  );
}

function Brand() {
  const { collapsed } = useAppShell();
  return (
    <Link
      to="/"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'flex-start',
        gap: 8,
        width: '100%',
        fontWeight: 700,
        fontSize: 15,
        letterSpacing: '-0.01em',
      }}
    >
      <img src={brand.assets.icon} alt="" width={22} height={22} style={{ objectFit: 'contain' }} />
      {collapsed ? null : brand.appName}
    </Link>
  );
}

const switcherButton: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 30,
  padding: '0 10px',
  border: '1px solid var(--vn-border-default)',
  borderRadius: 'var(--vn-radius-md)',
  background: 'var(--vn-bg-surface)',
  font: 'inherit',
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--vn-fg-default)',
  cursor: 'pointer',
};

function ProjectSwitcher() {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: projects = [], isLoading } = useMyProjects();

  // Reflect the project from the current /projects/:id route, if any.
  const activeId = location.pathname.match(/^\/projects\/([^/]+)/)?.[1];
  const active = projects.find((p) => p.id === activeId);
  const label = active?.name ?? (isLoading ? 'Loading…' : 'Projects');

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" style={switcherButton}>
          <FolderKanban size={14} color="var(--vn-fg-muted)" />
          {label}
          <ChevronsUpDown size={13} color="var(--vn-fg-subtle)" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuLabel>Projects</DropdownMenuLabel>
        {isLoading ? (
          <DropdownMenuItem disabled>Loading…</DropdownMenuItem>
        ) : projects.length === 0 ? (
          <DropdownMenuItem disabled>No projects yet</DropdownMenuItem>
        ) : (
          projects.slice(0, 8).map((p) => (
            <DropdownMenuItem key={p.id} onSelect={() => navigate(`/projects/${p.id}`)}>
              {p.name}
            </DropdownMenuItem>
          ))
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate('/projects')}>
          View all projects
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Lateral switch between the org's CDN orchestrators — rendered only inside a
 * CDN console (/services/cdn/:serviceId/*). Switching preserves the current
 * sub-page (Cache stays Cache on the next orchestrator), Cloudflare-style.
 */
function CdnServiceSwitcher() {
  const navigate = useNavigate();
  const location = useLocation();
  const match = location.pathname.match(/^\/services\/cdn\/([0-9a-f-]{36})(\/.*)?$/);
  const activeId = match?.[1];
  const subPath = match?.[2] ?? '';
  const { data } = useCdnUmbrellas(undefined, { enabled: !!activeId });
  const umbrellas = data?.umbrellas ?? [];

  // Hidden outside a console, and pointless with a single orchestrator.
  if (!activeId || umbrellas.length < 2) return null;
  const active = umbrellas.find((u) => u.service_id === activeId);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" style={switcherButton}>
          <Globe size={14} color="var(--vn-fg-muted)" />
          {active?.service_name ?? 'CDN'}
          <ChevronsUpDown size={13} color="var(--vn-fg-subtle)" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuLabel>CDN orchestrators</DropdownMenuLabel>
        {umbrellas.slice(0, 8).map((u) => (
          <DropdownMenuItem
            key={u.service_id}
            disabled={u.service_id === activeId}
            onSelect={() => navigate(`/services/cdn/${u.service_id}${subPath}`)}
          >
            {u.service_name}
            {u.is_shared ? ' · Shared' : ''}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate('/services/cdn')}>
          View all orchestrators
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Pin/unpin the service the current route is inside — the pinned set drives the
 * "Pinned" sidebar section and the dashboard block. Hidden outside a service
 * console (nothing to pin).
 */
function PinCurrentServiceButton() {
  const { pathname } = useLocation();
  const current = useCurrentServiceLeaf(pathname);
  const { isPinned, toggle } = usePinnedServices();
  if (!current) return null;
  const pinned = isPinned(current.id);
  return (
    <IconButton
      aria-label={pinned ? 'Unpin service' : 'Pin service'}
      title={pinned ? 'Unpin from sidebar & dashboard' : 'Pin to sidebar & dashboard'}
      variant="ghost"
      size="sm"
      className={styles.desktopOnly}
      onClick={() => toggle(current.id)}
    >
      <Star size={15} fill={pinned ? 'currentColor' : 'none'} />
    </IconButton>
  );
}

/**
 * Shows the account's WAAP subscription tier (Free / Standard / Pro / Custom).
 * The plan is per-WAAP-service, so we resolve the org's WAAP service and read its
 * active package. Hidden when the account has no WAAP service or no package set —
 * never a hardcoded placeholder. Clicking opens the plan settings.
 */
function WaapPackageBadge() {
  const navigate = useNavigate();
  const { data: services = [] } = useMyServices();
  // WAAP is the `cobra` provider — authoritative detection (a service's nav route
  // may fall back to the category when its scope isn't in the taxonomy).
  const waap = useMemo(() => services.find(isWaapCapable), [services]);
  const { data: packageId } = useWaapCurrentPackage(waap?.id);
  if (!waap || !packageId) return null;
  const name = WAAP_PLANS.find((p) => p.id === packageId)?.name ?? 'Custom';
  return (
    <button
      type="button"
      onClick={() => navigate(`${waapConsoleRoute(waap.id)}/settings`)}
      className={styles.desktopOnly}
      title="WAAP subscription — manage plan"
      style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer' }}
    >
      <Badge tone="neutral" appearance="outline">
        {name}
      </Badge>
    </button>
  );
}

function TopbarActions({ docsOpen, onToggleDocs }: { docsOpen: boolean; onToggleDocs: () => void }) {
  const navigate = useNavigate();
  return (
    <>
      {/* Documentation / Support — icon-only on mobile (label hidden, icon kept)
          so they stay reachable, not dropped. */}
      {brand.features.documentation && (
        <Button
          variant={docsOpen ? 'secondary' : 'ghost'}
          size="sm"
          startIcon={<BookOpen size={15} />}
          onClick={onToggleDocs}
          aria-label="Documentation"
        >
          <span className={styles.desktopOnly}>Documentation</span>
        </Button>
      )}
      {brand.features.support && (
        <Button
          variant="ghost"
          size="sm"
          startIcon={<LifeBuoy size={15} />}
          onClick={() => navigate('/support')}
          aria-label="Support"
        >
          <span className={styles.desktopOnly}>Support</span>
        </Button>
      )}
      <UserMenu />
    </>
  );
}

const searchTrigger: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  height: 32,
  padding: '0 8px',
  border: '1px solid var(--vn-border-strong)',
  borderRadius: 'var(--vn-radius-md)',
  background: 'var(--vn-bg-surface)',
  color: 'var(--vn-fg-subtle)',
  font: 'inherit',
  fontSize: 13,
  cursor: 'pointer',
  boxSizing: 'border-box',
};
const searchKbd: React.CSSProperties = {
  marginLeft: 'auto',
  fontSize: 11,
  color: 'var(--vn-fg-muted)',
  border: '1px solid var(--vn-border-default)',
  borderRadius: 'var(--vn-radius-xs)',
  padding: '1px 5px',
  background: 'var(--vn-bg-subtle)',
};

/**
 * Full-width strip shown while a DataCentral employee is impersonating this
 * customer, so it's obvious the session is staff-driven (not the customer's own
 * login) and who is behind it. Hidden for a direct customer login.
 */
function ImpersonationBanner() {
  const { isImpersonating, impersonatedBy } = useAuth();
  if (!isImpersonating || !impersonatedBy) return null;
  const who = impersonatedBy.name || impersonatedBy.email || `#${impersonatedBy.id}`;
  return (
    <div
      role="status"
      style={{
        padding: '8px 16px',
        background: 'var(--vn-warning-subtle)',
        borderBottom: '1px solid var(--vn-warning-border)',
        color: 'var(--vn-warning-text)',
        fontSize: 13,
        fontWeight: 600,
        textAlign: 'center',
      }}
    >
      DataCentral staff session — you are impersonating this account as{' '}
      <strong>{who}</strong>
      {impersonatedBy.email && impersonatedBy.name ? ` (${impersonatedBy.email})` : ''}. Actions
      are performed on the customer&apos;s account.
    </div>
  );
}

export function AppLayout() {
  return (
    <NavProvider>
      <PinnedServicesProvider>
        <AppLayoutInner />
      </PinnedServicesProvider>
    </NavProvider>
  );
}

function AppLayoutInner() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const [cmdkOpen, setCmdkOpen] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Close mobile nav on route change.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [pathname]);

  // A nested service console may register its own scope (sections + "Back to …").
  // When none is registered, fall back to the primary app nav.
  const scoped = useContextualNav();

  // The user's pinned services — surfaced as a top sidebar section (primary nav
  // only) and on the dashboard.
  const pinnedLeaves = usePinnedLeaves();

  // Provisioned services grouped by category (CDN collapsed to one umbrella
  // per org) — drives both the count badges and the click-through targets.
  const servicesTree = useServicesNavTree();
  const servicesByCategory = useMemo(
    () => new Map(servicesTree.map((cat) => [cat.key, cat.services])),
    [servicesTree],
  );

  // Cloudflare-style grouped nav: catalog categories are pulled OUT of a single
  // "Services" parent and clustered under uppercase group headings (Protect /
  // Infrastructure / Delivery / Observe & Manage, per SERVICE_GROUPS). Leaf ids stay
  // `services:<key>` / `services:all` so active-highlighting is unchanged.
  const primarySections = useMemo<ContextualNavSection[]>(() => {
    const enabledCats = brandServiceCategories();
    const catByKey = new Map(enabledCats.map((c) => [c.key, c]));
    const navLeaf = (item: (typeof PRIMARY_NAV)[number]): ContextualNavEntry => {
      const Icon = item.icon;
      return { id: item.to, label: item.label, icon: <Icon size={16} />, onSelect: () => navigate(item.to) };
    };
    // A category leaf: one provisioned service deep-links into its console; several
    // (or none) open the dashboard filtered to the category. Badge = provisioned
    // count (raw number — the sidebar wraps it in its own Badge).
    const categoryLeaf = (cat: (typeof enabledCats)[number]): ContextualNavEntry => {
      const CatIcon = cat.icon;
      const svcs = servicesByCategory.get(cat.key) ?? [];
      // Both badges pass a styled ReactNode so we bypass @ui's hardcoded blue
      // `tone="primary"` Badge: "Popular" uses brand-red; the numeric service
      // count uses a neutral tone-on-tone pill that blends with the sidebar.
      const badgeNode: React.ReactNode = cat.badge
        ? <span className={styles.popularBadge}>{cat.badge}</span>
        : svcs.length > 0
          ? <span className={styles.countBadge}>{svcs.length}</span>
          : undefined;
      return {
        id: `services:${cat.key}`,
        label: cat.title,
        icon: <CatIcon size={16} />,
        badge: badgeNode,
        // A category with a dedicated landing (e.g. compute, security) opens that
        // hub. Otherwise: one provisioned service deep-links into its console;
        // several (or none) open the dashboard filtered to the category.
        onSelect: () =>
          cat.landingRoute
            ? navigate(cat.landingRoute)
            : navigate(svcs.length === 1 ? svcs[0].route : `/services?category=${cat.key}`),
      };
    };
    const overview = PRIMARY_NAV.find((i) => i.to === '/');
    return [
      // Ungrouped top: Overview + the all-services dashboard.
      {
        items: [
          ...(overview ? [navLeaf(overview)] : []),
          {
            id: 'services:all',
            label: 'All services',
            icon: <Grip size={16} />,
            onSelect: () => navigate('/services'),
          },
        ],
      },
      // One section per top-level group — categories under an uppercase heading.
      // Groups with no brand-enabled category are omitted entirely (no empty headings).
      ...SERVICE_GROUPS.flatMap((g) => {
        const items = g.categoryKeys
          .map((k) => catByKey.get(k))
          .filter((c): c is (typeof enabledCats)[number] => !!c)
          .map(categoryLeaf);
        return items.length > 0 ? [{ heading: g.heading, items }] : [];
      }),
      // Support is reachable from the topbar action only — no duplicate sidebar
      // entry (both went to the same /support ticket page).
    ];
  }, [navigate, servicesByCategory]);

  const activePrimary = PRIMARY_NAV.find((item) => isActive(item, pathname))?.to;
  // Inside /services, highlight the nested category leaf instead of the parent.
  // A category leaf is also active when the current path is inside the console
  // of its single service (the deep-link case). Consoles that register their
  // own scoped nav bypass this entirely.
  const servicesCategory = new URLSearchParams(search).get('category');
  // A category whose dedicated landing (or a console beneath it) is open.
  const landedCategory = SERVICE_CATEGORIES.find(
    (c) =>
      c.landingRoute &&
      (pathname === c.landingRoute || pathname.startsWith(`${c.landingRoute}/`)),
  )?.key;
  const deepLinkedCategory = servicesTree.find(
    (cat) =>
      cat.services.length === 1 &&
      (pathname === cat.services[0].route ||
        pathname.startsWith(`${cat.services[0].route}/`)),
  )?.key;
  const primaryActiveId =
    activePrimary === '/services'
      ? landedCategory
        ? `services:${landedCategory}`
        : deepLinkedCategory
        ? `services:${deepLinkedCategory}`
        : pathname === '/services' &&
            servicesCategory &&
            SERVICE_CATEGORIES.some((c) => c.key === servicesCategory)
          ? `services:${servicesCategory}`
          : 'services:all'
      : activePrimary;

  const searchHeader = (
    <button type="button" style={searchTrigger} onClick={() => setCmdkOpen(true)}>
      <Search size={15} color="var(--vn-fg-muted)" />
      <span style={{ flex: 1, textAlign: 'left' }}>Quick search…</span>
      <kbd style={searchKbd}>⌘K</kbd>
    </button>
  );

  // Account is pinned to the bottom of the sidebar as a persistent shortcut,
  // floated apart from the primary items (see AppLayout.module.css). Omitted
  // inside the account scope, whose own sub-nav already covers it.
  const inAccountScope = pathname === '/account' || pathname.startsWith('/account/');
  const baseSections = scoped?.sections ?? primarySections;

  // "Pinned" shortcuts sit at the top of the PRIMARY nav (not inside a console's
  // own scoped nav, which stays focused on that service).
  const pinnedSection: ContextualNavSection | null =
    !scoped && !inAccountScope && pinnedLeaves.length > 0
      ? {
          heading: 'Pinned',
          items: pinnedLeaves.map((s) => ({
            id: `pinned:${s.id}`,
            label: s.label,
            icon: <Star size={16} />,
            onSelect: () => navigate(s.route),
          })),
        }
      : null;

  const sections: ContextualNavSection[] = inAccountScope
    ? baseSections
    : [
        ...(pinnedSection ? [pinnedSection] : []),
        ...baseSections,
        {
          items: [
            {
              id: '/account',
              label: 'Account',
              icon: <CircleUser size={16} />,
              onSelect: () => navigate('/account'),
            },
          ],
        },
      ];

  const closeMobileNav = () => setMobileNavOpen(false);

  return (
    <AppShell>
      <Topbar
        brand={
          <>
            {/* Hamburger — mobile only, on the LEFT (standard placement) */}
            <IconButton
              aria-label={mobileNavOpen ? 'Close navigation' : 'Open navigation'}
              variant="ghost"
              onClick={() => setMobileNavOpen((o) => !o)}
              className={styles.hamburger}
            >
              {mobileNavOpen ? <X size={18} /> : <Menu size={18} />}
            </IconButton>
            <Brand />
          </>
        }
        actions={
          <TopbarActions docsOpen={docsOpen} onToggleDocs={() => setDocsOpen((o) => !o)} />
        }
      >
        {/* Center items — hide lower-priority items on mobile */}
        <span className={styles.topbarCenter}>
          <ProjectSwitcher />
          <CdnServiceSwitcher />
          <PinCurrentServiceButton />
          <WaapPackageBadge />
        </span>
      </Topbar>
      <CommandMenu open={cmdkOpen} onOpenChange={setCmdkOpen} />
      <AppShellBody>
        {/* Mobile drawer backdrop — only rendered/visible on mobile when nav is open */}
        {mobileNavOpen && (
          <div
            className={styles.mobileBackdrop}
            aria-hidden="true"
            onClick={closeMobileNav}
          />
        )}

        <ContextualSidebar
          className={[
            inAccountScope ? undefined : styles.pinnedAccount,
            styles.sidebar,
            mobileNavOpen ? styles.sidebarOpen : undefined,
          ]
            .filter(Boolean)
            .join(' ')}
          aria-label={scoped ? 'Service' : 'Primary'}
          sections={sections}
          activeId={scoped ? scoped.activeId : primaryActiveId}
          back={scoped?.back}
          header={searchHeader}
          footer={<CollapseToggle />}
          onItemSelect={closeMobileNav}
        />
        <AppMain>
          <ImpersonationBanner />
          <Outlet />
        </AppMain>
        {docsOpen ? (
          <AppPanel aria-label="Documentation" width="440px">
            <DocsPanel onClose={() => setDocsOpen(false)} />
          </AppPanel>
        ) : null}
      </AppShellBody>
      {brand.features.livechat && <LiveChatWidget />}
    </AppShell>
  );
}
